import { IconNames } from '@blueprintjs/icons';
import { DropdownMenu } from '@strata/react-dropdown-menu';
import { Icon } from '@strata/react-status-icons';
import { SuccessToast } from '@strata/react-toast-message';
import { type ComponentProps, useState } from 'react';
import { useNavigate } from 'react-router';

import { SnoozeResearchDialog } from './dialogs/SnoozeResearchDialog';

import { Permissions } from 'services/constants';
import { AppRoutes } from 'services/routes';
import { type ClaimSummary, usePostUnsnoozeClaimMutation } from 'store/api/medbillingApi';
import { usePermissionCheck } from 'store/auth/authHelpers';

interface ClaimGridActionsMenuProps {
    claim: ClaimSummary;
}

export const ClaimGridActionsMenu = ({ claim }: ClaimGridActionsMenuProps) => {
    const navigate = useNavigate();
    const writeMedBilling = usePermissionCheck(Permissions.WRITE_MED_BILLING);
    const [isOpen, setIsOpen] = useState(false);

    const [postUnsnoozeClaim] = usePostUnsnoozeClaimMutation();

    const [isSnoozeResearchDialogOpen, setIsSnoozeResearchDialogOpen] = useState(false);

    const menuItems: ComponentProps<typeof DropdownMenu>['menuItemArray'] = [
        {
            text: 'View Details',
            onItemClick: () =>
                navigate(AppRoutes.claimDetails(claim.payer_id, claim.transaction_id)),
        },
    ];
    if (writeMedBilling) {
        if (!claim.snooze_date) {
            menuItems.push({
                text: SnoozeResearchDialog.Title,
                onItemClick: () => setIsSnoozeResearchDialogOpen(true),
            });
        } else {
            menuItems.push(
                {
                    text: 'Edit Snooze',
                    onItemClick: () => setIsSnoozeResearchDialogOpen(true),
                },
                {
                    text: 'Unsnooze Research',
                    onItemClick: () => {
                        void (async () => {
                            await postUnsnoozeClaim({
                                payerId: claim.payer_id,
                                transactionId: claim.transaction_id,
                            }).unwrap();
                            SuccessToast({
                                message:
                                    'Claim Research was Unsnoozed successfully. Based on filters this claim may have disappeared from view.',
                            });
                        })();
                    },
                },
            );
        }
    }

    return (
        <>
            <SnoozeResearchDialog
                gridAction
                isOpen={isSnoozeResearchDialogOpen}
                onClose={() => setIsSnoozeResearchDialogOpen(false)}
                payerId={claim.payer_id}
                transactionId={claim.transaction_id}
            />
            <DropdownMenu menuItemArray={menuItems} isOpen={isOpen} onInteraction={setIsOpen}>
                <Icon
                    title="Claim Actions"
                    icon={IconNames.MORE}
                    intent={writeMedBilling ? Icon.Intent.DEFAULT : Icon.Intent.SECONDARY}
                    onClick={writeMedBilling ? () => setIsOpen(true) : undefined}
                />
            </DropdownMenu>
        </>
    );
};
